<template>
    <section class="hero-container" :style="bannerStyle">
        <div v-if="hasGradient" class="hero-gradient" />
        <div class="hero-text-wrapper" :style="innerContainerStyle">
            <Typography v-bind="titleProps" class="title" v-html="$t(title)" />
            <Typography
                v-if="!hideSubTitle && subTitle"
                v-bind="subTitleProps"
                class="sub-title"
                v-html="$t(subTitle)"
            />
        </div>
        <LanguagePicker
            :dropDownStyles="dropDownStyleProps"
            v-if="showDropdown"
        />

        <DateDisplay
            v-if="$globalConstants.host.show_last_updated"
            :text-color="dateTextColor"
            :text-size="getDateTextSize"
        />
    </section>
</template>

<script>
import { heroBannerMixin, mediaQueriesMixin } from '@/mixins';
import { fontWeight } from '@/constants';
import { typographyTags } from '@atoms/Text/Typography';
import { HeroBannerAlphaProps } from '@organisms/HeroBanners/HeroBannerAlpha/HeroBannerAlphaProps';

export default {
    name: 'HeroBannerAlpha',
    components: {
        LanguagePicker: () => import('@/components/LanguagePicker'),
        DateDisplay: () => import('@atoms/DateDisplay/DateDisplay'),
    },
    mixins: [mediaQueriesMixin, heroBannerMixin],
    props: {
        title: {
            type: String,
        },
        subTitle: {
            type: String,
        },
        backgroundImage: {
            type: String,
        },
        ...HeroBannerAlphaProps,
    },
    data() {
        return {
            typographyTags,
        };
    },
    computed: {
        getDateTextSize() {
            return this.isDesktop()
                ? this.dateTextSize
                : this.dateMobileTextSize;
        },
        titleProps() {
            let commonProps = {
                tag: this.typographyTags.h1,
                color: this.titleColor,
                lineHeight: this.getTitleFontSize,
                size: this.getTitleFontSize,
                textAlign: this.titleAlign,
                weight: fontWeight.bold,
            };

            if (this.titleBottomShadow) {
                commonProps['color'] = 'white';
                commonProps['textAlign'] = 'center';
            }

            return commonProps;
        },
        subTitleProps() {
            return {
                tag: this.typographyTags.h2,
                color: this.subTitleColor,
                lineHeight: this.getSubTitleFontSize,
                size: this.getSubTitleFontSize,
                textAlign: this.subTitleAlign,
            };
        },
        titleBottomShadow() {
            return this.isMobileDevice() && this.headlineBottomShadow;
        },
        getTextWidth() {
            if (this.isMobile()) {
                return this.textWidthMobile;
            } else if (this.isTablet()) {
                return this.textWidthTablet;
            } else {
                return this.textWidth;
            }
        },
        innerContainerStyle() {
            if (this.titleBottomShadow) {
                return {
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    background: 'black',
                    opacity: 0.8,
                    padding: '5px',
                    width: this.getTextWidth,
                };
            }
            return {
                width: this.getTextWidth,
                paddingBottom: this.removeBottomPadding ? '0px' : '',
                background: this.textContainerBackground
                    ? this.textContainerBackground
                    : '',
            };
        },
        showBackgroundImage() {
            return (
                this.useBackgroundImage &&
                (!this.isMobile() || !this.hideBackgroundImageOnMobile)
            );
        },
        bannerStyle() {
            let commonProps = {
                height: this.getFixedHeight,
                'background-color': this.backgroundColor,
            };

            if (this.showBackgroundImage) {
                commonProps['background-color'] = 'transparent';
                commonProps[
                    'background-image'
                ] = `url("${this.backgroundImage}")`;
            }

            if (this.isMobileDevice() && this.mobileBackgroundPositionX) {
                commonProps['background-position-x'] =
                    this.mobileBackgroundPositionX;
            }

            return commonProps;
        },
        dropDownStyleProps() {
            return {
                headerBackgroundColor: this.dropDownHeaderBackgroundColor,
                headerTextColor: this.dropDownHeaderTextColor,
                backgroundColor: this.dropDownBackgroundColor,
                textColor: this.dropDownTextColor,
                textSize: this.dropDownTextSize,
            };
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@/sass/mixins';
@import '@/sass/pad-width-variables';

.hero-container {
    margin-bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    z-index: 2;
    background-position: center center;
    background-size: cover;
    background-repeat: no-repeat;

    .hero-gradient {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(180deg, rgba(0, 0, 0, 0.75), transparent);
    }
    .hero-text-wrapper {
        display: flex;
        flex-direction: column;
        max-width: 1200px;
        z-index: 1;
        padding: $mob-pad-width;
        @include mq('tablet') {
            padding: $tab-pad-width;
        }
    }
    .sub-title {
        margin-top: 20px;
        margin-left: 0px;
    }
}
</style>
